import React, { useState, useEffect, useMemo } from 'react';
import {
  Button, Grid, TextField, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Tooltip, ImageList, ImageListItem,
  Typography, Box, TableSortLabel,
} from '@mui/material';
import { Add, Edit, Delete, PhotoCamera } from '@mui/icons-material';
import { toast } from 'react-toastify';
import frotaService from '../../services/frotaService';
import {
  Veiculo, VeiculoEvento, VeiculoHistoricoItem, CategoriaHistoricoVeiculo,
  TIPOS_VEICULO_EVENTO, NIVEIS_COMBUSTIVEL,
  TipoVeiculoEvento, NivelCombustivel,
} from '../../types/frota';
import FuncionariosSelect from '../selects/FuncionariosSelect';
import { formatarData } from '../../utils/formatToView';

interface VeiculoHistoricoDialogProps {
  open: boolean;
  onClose: () => void;
  veiculo: Veiculo | null;
  onUpdated?: () => void;
}

const categoriaColor: Record<string, 'primary' | 'secondary' | 'info' | 'warning' | 'default' | 'error'> = {
  evento: 'primary',
  abastecimento: 'info',
  manutencao: 'warning',
  troca_oleo: 'secondary',
  multa: 'error',
};

const eventoTipoColor: Record<string, 'success' | 'warning' | 'default'> = {
  entrega: 'success',
  devolucao: 'warning',
  outro: 'default',
};

const FILTRO_CATEGORIAS: { value: string; label: string }[] = [
  { value: 'entrega', label: 'Entrega' },
  { value: 'devolucao', label: 'Devolução' },
  { value: 'outro', label: 'Outro' },
  { value: 'abastecimento', label: 'Abastecimento' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'troca_oleo', label: 'Troca de óleo' },
  { value: 'multa', label: 'Multa' },
];

const matchCategoriaFiltro = (item: VeiculoHistoricoItem, filtro: string): boolean => {
  if (!filtro) return true;
  if (['entrega', 'devolucao', 'outro'].includes(filtro)) {
    return item.categoria === 'evento' && (item.raw as VeiculoEvento).tipo === filtro;
  }
  return item.categoria === (filtro as CategoriaHistoricoVeiculo);
};

const getIntervaloMesAtual = () => {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth();
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return {
    inicio: fmt(new Date(ano, mes, 1)),
    fim: fmt(new Date(ano, mes + 1, 0)),
  };
};

const VeiculoHistoricoDialog: React.FC<VeiculoHistoricoDialogProps> = ({
  open, onClose, veiculo, onUpdated,
}) => {
  const [historico, setHistorico] = useState<VeiculoHistoricoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroMotoristaId, setFiltroMotoristaId] = useState<number | null>(null);
  const [ordenacaoData, setOrdenacaoData] = useState<'asc' | 'desc'>('asc');
  const [openForm, setOpenForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedEvento, setSelectedEvento] = useState<VeiculoEvento | null>(null);
  const [fotos, setFotos] = useState<File[]>([]);
  const [form, setForm] = useState({
    tipo: 'entrega' as TipoVeiculoEvento,
    funcionario_id: null as number | null,
    kilometragem: 0,
    nivel_combustivel: '' as NivelCombustivel | '',
    data_evento: new Date().toISOString().split('T')[0],
    observacoes: '',
  });

  const loadHistorico = async () => {
    if (!veiculo) return;
    try {
      setLoading(true);
      const items = await frotaService.listarHistoricoVeiculo(veiculo.id);
      setHistorico(items);
    } catch {
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && veiculo) {
      const { inicio, fim } = getIntervaloMesAtual();
      setFiltroDataInicio(inicio);
      setFiltroDataFim(fim);
      setFiltroCategoria('');
      setFiltroMotoristaId(null);
      setOrdenacaoData('asc');
      loadHistorico();
    }
  }, [open, veiculo?.id]);

  const historicoFiltrado = useMemo(() => {
    const filtrado = historico.filter((item) => {
      if (filtroDataInicio && item.data < filtroDataInicio) return false;
      if (filtroDataFim && item.data > filtroDataFim) return false;
      if (!matchCategoriaFiltro(item, filtroCategoria)) return false;
      if (filtroMotoristaId && item.motorista_id !== filtroMotoristaId) return false;
      return true;
    });

    return [...filtrado].sort((a, b) => {
      const cmp = a.data.localeCompare(b.data);
      return ordenacaoData === 'asc' ? cmp : -cmp;
    });
  }, [historico, filtroDataInicio, filtroDataFim, filtroCategoria, filtroMotoristaId, ordenacaoData]);

  const resetForm = () => {
    setFotos([]);
    setForm({
      tipo: 'entrega',
      funcionario_id: null,
      kilometragem: veiculo?.kilometragem_atual || 0,
      nivel_combustivel: '',
      data_evento: new Date().toISOString().split('T')[0],
      observacoes: '',
    });
  };

  const openEditEvento = (item: VeiculoHistoricoItem) => {
    const e = item.raw as VeiculoEvento;
    setSelectedEvento(e);
    setFotos([]);
    setForm({
      tipo: e.tipo,
      funcionario_id: e.funcionario_id || null,
      kilometragem: e.kilometragem || 0,
      nivel_combustivel: e.nivel_combustivel || '',
      data_evento: e.data_evento,
      observacoes: e.observacoes || '',
    });
    setOpenForm(true);
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('veiculo_id', String(veiculo!.id));
    fd.append('tipo', form.tipo);
    if (form.funcionario_id) fd.append('funcionario_id', String(form.funcionario_id));
    if (form.kilometragem) fd.append('kilometragem', String(form.kilometragem));
    if (form.nivel_combustivel) fd.append('nivel_combustivel', form.nivel_combustivel);
    fd.append('data_evento', form.data_evento);
    if (form.observacoes) fd.append('observacoes', form.observacoes);
    fotos.forEach((f) => fd.append('fotos', f));
    return fd;
  };

  const handleSaveEvento = async () => {
    if (!veiculo || !form.data_evento) {
      toast.error('Preencha a data do evento');
      return;
    }
    if (form.tipo === 'entrega' && !form.funcionario_id) {
      toast.error('Informe o motorista para entrega');
      return;
    }
    try {
      const fd = buildFormData();
      if (selectedEvento) {
        await frotaService.atualizarEvento(selectedEvento.id, fd);
        toast.success('Evento atualizado');
      } else {
        await frotaService.criarEvento(fd);
        toast.success('Evento registrado');
      }
      setOpenForm(false);
      setSelectedEvento(null);
      await loadHistorico();
      onUpdated?.();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao salvar evento');
    }
  };

  const handleDeleteEvento = async () => {
    if (!selectedEvento) return;
    try {
      await frotaService.excluirEvento(selectedEvento.id);
      toast.success('Evento excluído');
      setOpenDelete(false);
      setSelectedEvento(null);
      await loadHistorico();
      onUpdated?.();
    } catch {
      toast.error('Erro ao excluir evento');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Histórico — {veiculo?.placa} {veiculo?.marca} {veiculo?.modelo}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body2" color="text.secondary">
              Eventos, abastecimentos, manutenções, trocas de óleo e multas ordenados por data
            </Typography>
            <Button
              size="small"
              startIcon={<Add />}
              variant="contained"
              onClick={() => { resetForm(); setSelectedEvento(null); setOpenForm(true); }}
            >
              Novo evento
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth size="small" type="date" label="Data inicial"
                InputLabelProps={{ shrink: true }}
                value={filtroDataInicio}
                onChange={(e) => setFiltroDataInicio(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth size="small" type="date" label="Data final"
                InputLabelProps={{ shrink: true }}
                value={filtroDataFim}
                onChange={(e) => setFiltroDataFim(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Categoria</InputLabel>
                <Select
                  value={filtroCategoria}
                  label="Categoria"
                  displayEmpty
                  onChange={(e) => setFiltroCategoria(e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {FILTRO_CATEGORIAS.map((c) => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FuncionariosSelect
                label="Motorista"
                value={filtroMotoristaId}
                onChange={setFiltroMotoristaId}
                allowClear
                placeholder="Todos"
              />
            </Grid>
          </Grid>

          {loading ? <CircularProgress size={28} /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active
                      direction={ordenacaoData}
                      onClick={() => setOrdenacaoData((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                    >
                      Data
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Motorista</TableCell>
                  <TableCell>KM</TableCell>
                  <TableCell>Detalhes</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historicoFiltrado.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{formatarData(item.data)}</TableCell>
                    <TableCell>
                      {item.categoria === 'evento' ? (
                        <Chip
                          size="small"
                          label={item.titulo}
                          color={eventoTipoColor[(item.raw as VeiculoEvento).tipo] || 'default'}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label={frotaService.categoriaHistoricoLabel(item.categoria)}
                          color={categoriaColor[item.categoria]}
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>{item.categoria === 'evento' ? '-' : item.titulo}</TableCell>
                    <TableCell>{item.motorista || '-'}</TableCell>
                    <TableCell>{item.km ?? '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" noWrap title={item.detalhes}>{item.detalhes}</Typography>
                      {item.anexos && item.anexos.length > 0 && (
                        <ImageList cols={3} rowHeight={60} sx={{ mt: 1, maxHeight: 70 }}>
                          {item.anexos.slice(0, 3).map((a) => (
                            <ImageListItem key={a.id}>
                              <img
                                src={frotaService.getUploadUrl(a.url)}
                                alt={a.nome_arquivo || 'Foto'}
                                loading="lazy"
                                style={{ objectFit: 'cover', borderRadius: 4 }}
                              />
                            </ImageListItem>
                          ))}
                        </ImageList>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.status ? <Chip size="small" label={item.status} /> : '-'}
                      {item.valor ? (
                        <Typography variant="caption" display="block">{frotaService.formatCurrency(item.valor)}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      {item.categoria === 'evento' && (
                        <>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => openEditEvento(item)}><Edit /></IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedEvento(item.raw as VeiculoEvento);
                                setOpenDelete(true);
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {historicoFiltrado.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Alert severity="info">
                        {historico.length === 0 ? 'Nenhum registro para este veículo' : 'Nenhum registro com os filtros aplicados'}
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedEvento ? 'Editar evento' : 'Novo evento'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de evento</InputLabel>
                <Select
                  size="small"
                  value={form.tipo}
                  label="Tipo de evento"
                  disabled={!!selectedEvento}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value as TipoVeiculoEvento })}
                >
                  {TIPOS_VEICULO_EVENTO.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FuncionariosSelect
                label="Motorista responsável"
                value={form.funcionario_id}
                onChange={(v) => setForm({ ...form, funcionario_id: v })}
                required={form.tipo === 'entrega'}
                disabled={form.tipo === 'devolucao'}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                size="small" fullWidth type="date" label="Data"
                InputLabelProps={{ shrink: true }}
                value={form.data_evento}
                onChange={(e) => setForm({ ...form, data_evento: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                size="small" fullWidth type="number" label="Kilometragem"
                value={form.kilometragem}
                onChange={(e) => setForm({ ...form, kilometragem: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Nível do tanque</InputLabel>
                <Select
                  size="small"
                  value={form.nivel_combustivel}
                  label="Nível do tanque"
                  onChange={(e) => setForm({ ...form, nivel_combustivel: e.target.value as NivelCombustivel })}
                >
                  <MenuItem value=""><em>Não informado</em></MenuItem>
                  {NIVEIS_COMBUSTIVEL.map((n) => (
                    <MenuItem key={n.value} value={n.value}>{n.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small" fullWidth multiline rows={2} label="Observações"
                value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Button component="label" variant="outlined" startIcon={<PhotoCamera />}>
                Anexar fotos
                <input type="file" hidden multiple accept="image/*" onChange={(e) => e.target.files && setFotos(Array.from(e.target.files))} />
              </Button>
              {fotos.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>{fotos.length} foto(s) selecionada(s)</Typography>
              )}
            </Grid>
            {selectedEvento?.anexos && selectedEvento.anexos.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Fotos existentes</Typography>
                <ImageList cols={4} rowHeight={100} sx={{ maxHeight: 200 }}>
                  {selectedEvento.anexos.map((a) => (
                    <ImageListItem key={a.id}>
                      <img src={frotaService.getUploadUrl(a.url)} alt={a.nome_arquivo || 'Foto'} loading="lazy" style={{ objectFit: 'cover' }} />
                    </ImageListItem>
                  ))}
                </ImageList>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveEvento}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Excluir evento?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDeleteEvento}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VeiculoHistoricoDialog;
